import { TranslationFunction } from '@/hooks/useTranslation';
import { Provider, Treatment, Doctor } from '@/types/database';
import { createIdMap } from '@/utils/hashMapHelpers';

type Equipment = Record<string, unknown>; // TODO: Add proper equipment type

/**
 * Derives provider brand name from URL slug for ProviderDetail pages
 * @param currentPageName - The current page name
 * @param pathname - The current URL pathname
 * @returns The derived brand name or null
 */
export const deriveProviderBrandName = (
  currentPageName: string,
  pathname: string
): string | null => {
  try {
    if (currentPageName !== 'ProviderDetail') return null;
    const path = pathname;
    const slug = path.split('/').pop();
    if (!slug) return null;
    // Remove trailing short ID (e.g., -abcdef12)
    const namePart = slug.replace(/-[\da-f]{8}$/i, '');
    if (!namePart) return null;
    // Convert kebab-case to Title Case
    const titleCase = namePart
      .split('-')
      .map(p => (p ? p.charAt(0).toUpperCase() + p.slice(1) : p))
      .join(' ');
    return titleCase;
  } catch {
    return null;
  }
};

// Get the module order from provider data or use default order
// Support both old array format and new language-specific object format
const ensureMediaModules = (order: string | string[] | null | undefined): string[] => {
  // Normalize input: accept arrays, comma-separated strings, or falsy
  let normalized: string[] = [];
  if (Array.isArray(order)) {
    normalized = order.filter(Boolean);
  } else if (typeof order === 'string') {
    normalized = order
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0);
  }

  const result = [...normalized];

  const insertModule = (list: string[], module: string, anchors: string[] = []): string[] => {
    if (list.includes(module)) return list;
    const copy = [...list];
    for (const anchor of anchors) {
      const idx = copy.indexOf(anchor);
      if (idx !== -1) {
        copy.splice(idx + 1, 0, module);
        return copy;
      }
    }
    copy.push(module);
    return copy;
  };

  const withEquipment = insertModule(result, 'equipment', ['packages', 'treatments', 'doctors']);
  const withVideos = insertModule(withEquipment, 'videos', ['equipment', 'packages', 'treatments']);
  const withTimeline = insertModule(withVideos, 'timeline', [
    'videos',
    'press',
    'articles',
    'testimonials',
  ]);
  return withTimeline;
};

type ModuleOrderData = {
  order?: string | string[];
  hidden?: string;
};

export type ProviderWithModuleOrder = {
  module_order?: string | Record<string, ModuleOrderData>;
};

const getDefaultModuleOrder = (): { order: string[]; hidden: string[] } => {
  const defaultOrderArray = ensureMediaModules([
    'about',
    'before_after',
    'certifications',
    'affiliations',
    'partnerships',
    'treatments',
    'doctors',
    'packages',
    'press',
    'faq',
    'testimonials',
    'articles',
    'timeline',
  ]);
  return { order: defaultOrderArray, hidden: [] };
};

const getSafeLanguageData = (
  moduleOrder: Record<string, unknown>,
  currentLanguage: string,
  defaultObj: { order: string[]; hidden: string[] }
) => {
  const validLanguages = ['en', 'ko', 'zh', 'ja', 'vi', 'ru', 'mn', 'ar', 'th', 'id', 'es'];
  const safeLang = validLanguages.includes(currentLanguage) ? currentLanguage : 'en';
  return moduleOrder[safeLang] ?? moduleOrder.en ?? defaultObj;
};

interface LanguageData {
  order?: string | string[] | null;
  hidden?: string[] | null;
}

const processLanguageData = (
  languageData: unknown,
  defaultObj: { order: string[]; hidden: string[] }
): { order: string[]; hidden: string[] } => {
  // If languageData is an array, normalize to object
  if (Array.isArray(languageData)) {
    return { order: ensureMediaModules(languageData), hidden: [] };
  }

  const data = languageData as LanguageData | null | undefined;
  const order = ensureMediaModules(data?.order ?? defaultObj.order);
  const hidden = Array.isArray(data?.hidden) ? data.hidden : [];
  return { order, hidden };
};

export const getModuleOrder = (
  provider: ProviderWithModuleOrder | null | undefined,
  currentLanguage: string
): { order: string[]; hidden: string[] } => {
  const defaultObj = getDefaultModuleOrder();

  if (!provider?.module_order) {
    return defaultObj;
  }

  // Old format: array
  if (Array.isArray(provider.module_order)) {
    return { order: ensureMediaModules(provider.module_order), hidden: [] };
  }

  // New format: object with language keys
  if (typeof provider.module_order === 'object') {
    const languageData = getSafeLanguageData(provider.module_order, currentLanguage, defaultObj);
    return processLanguageData(languageData, defaultObj);
  }

  return defaultObj;
};

// Helper function to check array data and update counts/hasData
const checkArrayData = (
  key: string,
  data: unknown,
  counts: Record<string, number>,
  hasData: Record<string, boolean>
): void => {
  if (data && Array.isArray(data) && data.length > 0) {
    counts[key] = data.length;
    hasData[key] = true;
  }
};

// Helper function to check provider array property
const checkProviderArray = (
  key: string,
  property: string,
  provider: Provider,
  counts: Record<string, number>,
  hasData: Record<string, boolean>
): void => {
  // Property mapping for safe access
  const propertyMap: Record<string, unknown> = {
    before_after_photos: provider.before_after_photos,
    certifications: provider.certifications,
    affiliations: provider.affiliations,
    partnerships: provider.partnerships,
    packages: provider.packages,
    videos: provider.videos,
    press_news: provider.press_news,
    patient_testimonials: provider.patient_testimonials,
    articles: provider.articles,
  };

  const data = propertyMap[property] ?? (provider as unknown as Record<string, unknown>)[property];

  if (data && Array.isArray(data) && data.length > 0) {
    counts[key] = data.length;
    hasData[key] = true;
  }
};

// Calculate FAQ count from provider data
const calculateFaqCount = (
  provider: Provider,
  counts: Record<string, number>,
  hasData: Record<string, boolean>
): void => {
  if (provider.provider_faqs) {
    const faqs = provider.provider_faqs as Record<string, unknown>;

    const sections =
      faqs.sections && Array.isArray(faqs.sections)
        ? (faqs.sections as Record<string, unknown>[])
        : [{ faqs: faqs.faqs as unknown[] }];
    const totalFaqs = sections.reduce(
      (sum: number, section: Record<string, unknown>) =>
        sum + (Array.isArray(section.faqs) ? section.faqs.length : 0),
      0
    );
    if (totalFaqs > 0) {
      counts.faq = totalFaqs;

      hasData.faq = true;
    }
  }
};

// Calculate counts and availability for sections based on data presence
const calculateSectionData = (
  provider: Provider | null | undefined,
  treatments: Treatment[] | null | undefined,
  doctors: Doctor[] | null | undefined,
  providerEquipment: Equipment[]
): { counts: Record<string, number>; hasData: Record<string, boolean> } => {
  const counts: Record<string, number> = {};
  const hasData: Record<string, boolean> = {};

  if (provider) {
    // About section is always available
    hasData.about = true;

    // Check data availability for each section
    checkArrayData('treatments', treatments, counts, hasData);
    checkArrayData('doctors', doctors, counts, hasData);
    checkProviderArray('before_after', 'before_after_photos', provider, counts, hasData);
    checkProviderArray('certifications', 'certifications', provider, counts, hasData);
    checkProviderArray('affiliations', 'affiliations', provider, counts, hasData);
    checkProviderArray('partnerships', 'partnerships', provider, counts, hasData);
    checkProviderArray('packages', 'packages', provider, counts, hasData);
    checkArrayData('equipment', providerEquipment, counts, hasData);
    checkProviderArray('videos', 'videos', provider, counts, hasData);
    checkProviderArray('press', 'press_news', provider, counts, hasData);
    checkProviderArray('testimonials', 'patient_testimonials', provider, counts, hasData);
    checkProviderArray('articles', 'articles', provider, counts, hasData);

    // Timeline availability
    if (provider.timeline && Array.isArray(provider.timeline) && provider.timeline.length > 0) {
      counts.timeline = provider.timeline.length;
      hasData.timeline = true;
    }

    // FAQ count
    calculateFaqCount(provider, counts, hasData);
  }

  return { counts, hasData };
};

// Build the section map object based on counts and hasData
const buildSectionMap = (
  counts: Record<string, number>,
  hasData: Record<string, boolean>,
  t?: TranslationFunction
) => {
  // Use translation function if provided, otherwise fallback to English
  const getLabel = (key: string, defaultLabel: string) =>
    t ? t('provider_detail', `section_${key}`, defaultLabel) : defaultLabel;

  // Helper to create section objects
  const createSection = (key: string, defaultLabel: string, includeCount = false) => ({
    id: key.replace('_', '-'),
    label: getLabel(key, defaultLabel),
    isAvailable: hasData[key] ?? false,
    ...(includeCount && {
      count: counts[key],
    }),
  });

  // Return sections based on data availability rather than DOM rendering
  return {
    about: createSection('about', 'About'),
    before_after: createSection('before_after', 'Gallery', true),
    certifications: createSection('certifications', 'Certifications', true),
    affiliations: createSection('affiliations', 'Affiliations', true),
    partnerships: createSection('partnerships', 'Partnerships', true),
    treatments: createSection('treatments', 'Treatments', true),
    doctors: createSection('doctors', 'Doctors', true),
    packages: createSection('packages', 'Packages', true),
    equipment: createSection('equipment', 'Equipment', true),
    videos: createSection('videos', 'Videos', true),
    press: createSection('press', 'Press', true),
    faq: createSection('faq', 'FAQ', true),
    testimonials: createSection('testimonials', 'Testimonials', true),
    articles: createSection('articles', 'Articles', true),
    timeline: createSection('timeline', 'Timeline', true),
  };
};

// Get section mapping for navigation - based on available data (not DOM rendering)
export const getSectionMap = (
  renderedSections: string[],
  provider: Provider | null | undefined,
  treatments: Treatment[] | null | undefined,
  doctors: Doctor[] | null | undefined,
  providerEquipment: Equipment[] = [],
  t?: TranslationFunction
) => {
  const { counts, hasData } = calculateSectionData(
    provider,
    treatments,
    doctors,
    providerEquipment
  );
  return buildSectionMap(counts, hasData, t);
};

// Calculate treatment count for a provider (used in treatment search mode)
export const getTreatmentCount = (providers: Provider[], providerId: string | number) => {
  // When in "treatment-search" mode, 'providers' state holds the list of providers.
  // Each provider object in this list should have 'available_treatments'.
  // Use O(1) map lookup instead of O(n) find() operation
  const providerMap = createIdMap(providers, p => p.id);
  const foundProvider = providerMap.get(String(providerId));
  if (foundProvider?.available_treatments && Array.isArray(foundProvider.available_treatments)) {
    return foundProvider.available_treatments.length;
  }
  // Fallback if available_treatments is not present or provider not found
  return 0;
};

const isValidUrlString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const extractUrlFromLogoObject = (value: unknown): string | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const possibleKeys = ['url', 'src', 'href', 'image', 'image_url'];
  const obj = value as Record<string, unknown>;
  for (const key of possibleKeys) {
    const candidate = obj[key];
    if (isValidUrlString(candidate)) {
      return candidate.trim();
    }
  }

  return null;
};

const collectLogoCandidates = (provider: Provider, candidateList: string[]): void => {
  const addCandidate = (candidate: unknown): void => {
    if (!candidate) return;

    if (typeof candidate === 'string') {
      if (isValidUrlString(candidate)) {
        candidateList.push(candidate.trim());
      }
      return;
    }

    const extracted = extractUrlFromLogoObject(candidate);
    if (extracted) {
      candidateList.push(extracted);
    }
  };

  // Only use the actual logo_url property from Provider interface
  addCandidate(provider.logo_url);

  // Check for branding information in JSON fields
  const branding =
    (provider as unknown as Record<string, unknown>).branding ??
    (provider as unknown as Record<string, unknown>).provider_branding ??
    (provider as unknown as Record<string, unknown>).branding_info;
  if (branding) {
    const brandingObj = branding as Record<string, unknown>;
    addCandidate(brandingObj.logo_url as string);
  }
};

export const getProviderPrimaryLogoUrl = (provider: Provider | null | undefined): string | null => {
  if (!provider) return null;

  const candidates: string[] = [];
  collectLogoCandidates(provider, candidates);

  const firstValid = candidates.find(isValidUrlString);
  return firstValid ? firstValid.trim() : null;
};
